import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import {
	getAIFCContentType,
	isCreatableAction,
	isUpdatableAction,
} from '@atlassian/conversation-assistant-action-helpers/utils';
import {
	customActionUpdater,
	getAccumulatedActionTypeGuard,
	getDynamicUiType,
	getPrimaryAccumulatedValue,
	isAccumulatedAction,
	isRemixGeneratedFlow,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import type { CreationContextParams } from '@atlassian/conversation-assistant-external-context/types';
import {
	ExperienceName,
	PreviewCardRenderActionAttribute,
	PreviewCardRenderStage,
	remixEditExistingExperience,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import type { ExperienceTrackerAPI } from '@atlassian/conversation-assistant-instrumentation/main';
import {
	isStreamActionClassification,
	isStreamActionComplete,
	isStreamActionPart,
	isStreamActionPrepare,
	type StreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';

import { createAifcCreateInChatExperienceCollect } from '../../common/utils/aifc-experience-collect/create-aifc-create-in-chat-experience-collect';
import { startUpdateActionExperiences } from '../../common/utils/aifc-experience-collect/start-update-action-experiences';
import type { PromptStreamHook } from '../bridge-api/handlers/use-prompt-stream';

import { addActionPart } from './addActionPart';
import { findLastAssistantAction } from './findLastAssistantAction';
import { getActionPartKeyForMetrics } from './getActionPartKeyForMetrics';
import type { SendMessageTrackingAttributes, StreamActionInfo } from './sendMessageActionTypes';
import type { ChatStoreContainerProps, MessageSource } from './types';

type ProcessAifcActionStreamMessageParams = {
	streamMessage: StreamMessage;
	conversationId: string;
	tmpAssistantMessageId: string;
	traceId: string | undefined;
	productKey: string | undefined;
	deepResearchEnabled: boolean | undefined;
	storeUserMessages: boolean;
	streamActionInfo: StreamActionInfo;
	trackingAttributes: Pick<
		SendMessageTrackingAttributes,
		'forcedContentType' | 'requestId' | 'instrumentationId'
	>;
	experienceTracker: ExperienceTrackerAPI;
	chatContextStoreActions: ChatStoreContainerProps['chatContextStoreActions'];
	getMessages: ChatStoreContainerProps['getMessages'];
	setMessages: ChatStoreContainerProps['setMessages'];
	sendAnalyticsEvent: ChatStoreContainerProps['sendAnalyticsEvent'];
	getIsChatMounted?: ChatStoreContainerProps['getIsChatMounted'];
	processMessageStreamAction: ChatStoreContainerProps['processMessageStreamAction'];
	processMessageStreamComplete: ChatStoreContainerProps['processMessageStreamComplete'];
	processMessageStreamActionClassification: ChatStoreContainerProps['processMessageStreamActionClassification'];
	promptStreamHook?: PromptStreamHook | undefined;
	openChat?: boolean;
	messageSources: Partial<MessageSource> | undefined;
	creationContextParams?: CreationContextParams;
	agentId?: string;
	agentName?: string;
	agentIdentityAccountId?: string | null | undefined;
	stopInteractionMetricsTimeToFirstActionPart: (
		result: 'cancel' | 'stop',
		customData?: Record<string, unknown>,
	) => void;
};

/**
 * Recorded when an action chunk carries no `status`, so that an absent status attribute can be
 * told apart from a stage that was never reached.
 */
const MISSING_ACTION_STATUS = 'missing';

/**
 * Handles a single AIFC action stream message: starts/updates the relevant
 * AIFC experiences, forwards action parts/classification/completion to the
 * editor bridge, and writes the action parts into the message store.
 *
 * Shared by `processStream` (send-message flow) and the confirmed-tools flow so
 * both stream content into the page and emit identical AIFC experiences.
 *
 * Mutates `streamActionInfo` in place. Returns which action signals were seen so
 * callers can update their loop-scoped flags.
 */
export const processAifcActionStreamMessage = ({
	streamMessage,
	conversationId,
	tmpAssistantMessageId,
	traceId,
	productKey,
	deepResearchEnabled,
	storeUserMessages,
	streamActionInfo,
	trackingAttributes,
	experienceTracker,
	chatContextStoreActions,
	getMessages,
	setMessages,
	sendAnalyticsEvent,
	getIsChatMounted,
	processMessageStreamAction,
	processMessageStreamComplete,
	processMessageStreamActionClassification,
	promptStreamHook,
	openChat,
	messageSources,
	creationContextParams,
	agentId,
	agentName,
	agentIdentityAccountId,
	stopInteractionMetricsTimeToFirstActionPart,
}: ProcessAifcActionStreamMessageParams): {
	hasReceivedActionClassification: boolean;
	hasReceivedActionComplete: boolean;
} => {
	let hasReceivedActionClassification = false;
	let hasReceivedActionComplete = false;

	let commonExperienceAttributes: Record<string, unknown> = {
		llmDebugLogsEnabled: storeUserMessages,
	};
	if (isStreamActionClassification(streamMessage)) {
		promptStreamHook?.onActionClassification?.(
			streamMessage,
			tmpAssistantMessageId,
			conversationId,
		);

		const actionKey = streamMessage.message.action_key;
		hasReceivedActionClassification = true;
		const actualContentType = getAIFCContentType({ key: actionKey });
		commonExperienceAttributes = {
			...commonExperienceAttributes,
			actionKey,
			contentType: actualContentType,
			isStagingArea: chatContextStoreActions.getIsStagingArea(),
			humanMessageCount: getMessages(conversationId).filter((m) => m.type === 'HUMAN').length,
			product: productKey,
		};

		if (trackingAttributes.forcedContentType) {
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'compared',
				actionSubject: 'forcedContentType',
				actionSubjectId: 'contentTypeMatch',
				attributes: {
					forcedContentType: trackingAttributes.forcedContentType,
					actualContentType,
					matched: actualContentType.endsWith(`-${trackingAttributes.forcedContentType}`),
					actionKey,
				},
			});
		}

		const isUpdateAction = isUpdatableAction({ key: actionKey });
		if (
			isUpdateAction &&
			(!commonExperienceAttributes.isStagingArea ||
				(commonExperienceAttributes.humanMessageCount as number) > 1)
		) {
			const rootExperienceName = commonExperienceAttributes.isStagingArea
				? ExperienceName.AIFC_UPDATE_CONTENT
				: ExperienceName.UPDATE_CONTENT_IN_CHAT;

			startUpdateActionExperiences({
				experienceTracker,
				conversationId,
				rootExperienceName,
				commonExperienceAttributes,
			});
		}

		const isCreateAction = isCreatableAction({ key: actionKey });
		if (isCreateAction) {
			// initial prompt from staging area has its own AIFC_GENERATE_CONTENT experience - dont start this one
			if (
				!commonExperienceAttributes.isStagingArea ||
				(commonExperienceAttributes.humanMessageCount as number) > 1
			) {
				experienceTracker.start({
					name: ExperienceName.AIFC_GENERATE_CONTENT_IN_CHAT,
					id: conversationId,
					collect: createAifcCreateInChatExperienceCollect(),
					attributes: commonExperienceAttributes,
				});

				experienceTracker.start({
					name: ExperienceName.AIFC_MESSAGE,
					id: conversationId,
					attributes: {
						...commonExperienceAttributes,
						parentExperienceId: ExperienceName.AIFC_GENERATE_CONTENT_IN_CHAT,
					},
				});
				experienceTracker.start({
					name: ExperienceName.PREVIEW_CARD_RENDER,
					id: conversationId,
					timeout: deepResearchEnabled ? undefined : 60_000, // 60 second timeout
					attributes: {
						...commonExperienceAttributes,
						parentExperienceId: ExperienceName.AIFC_GENERATE_CONTENT_IN_CHAT,
						[PreviewCardRenderStage.STARTED_AT]: performance.now(),
					},
				});
				// Product AIFC integration is responsible for succeeding this experience.
				experienceTracker.start({
					name: ExperienceName.AIFC_ALL_DATA_HANDLED,
					id: conversationId,
					attributes: {
						...commonExperienceAttributes,
						parentExperienceId: ExperienceName.AIFC_GENERATE_CONTENT_IN_CHAT,
					},
				});
			} else {
				// update AIFC_GENERATE_CONTENT now that intent detection is complete. This experience starts as the user sends the message on the initial prompt screen
				experienceTracker.updateAttributes({
					name: ExperienceName.AIFC_GENERATE_CONTENT,
					attributes: commonExperienceAttributes,
				});

				experienceTracker.updateAttributes({
					name: ExperienceName.AIFC_INITIAL_DATA_RECEIVED,
					attributes: commonExperienceAttributes,
				});
			}
		}
	}

	if (isStreamActionPart(streamMessage) || isStreamActionPrepare(streamMessage)) {
		// Update action tracking info
		if (!streamActionInfo.isAction) {
			streamActionInfo.isAction = true;
			streamActionInfo.actionKey = streamMessage.message.key;
		}

		// track attribute for action prepare
		if (
			isStreamActionPrepare(streamMessage) &&
			expValEquals('cc-maui-experiment', 'isEnabled', true)
		) {
			const actionData = streamMessage.message.data as { dynamicUiSubtype?: string } | undefined;
			experienceTracker.updateAttributes({
				name: ExperienceName.AIFC_MESSAGE,
				attributes: { dynamicUiSubtype: actionData?.dynamicUiSubtype },
			});

			// Remix flows skip ACTION_CLASSIFICATION and go straight to ACTION_PREPARE,
			// so we start PREVIEW_CARD_RENDER here for remix flows.
			// Only start when rovo chat is mounted/visible to the user.
			if (
				getIsChatMounted?.() &&
				isRemixGeneratedFlow({
					key: streamMessage.message.key,
					data: streamMessage.message.data,
				})
			) {
				const remixActionKey = streamMessage.message.key;
				const actualContentType = getAIFCContentType({ key: remixActionKey });
				experienceTracker.start({
					name: ExperienceName.PREVIEW_CARD_RENDER,
					id: conversationId,
					timeout: 60_000,
					attributes: {
						...commonExperienceAttributes,
						actionKey: remixActionKey,
						contentType: actualContentType,
						isStagingArea: chatContextStoreActions.getIsStagingArea(),
						humanMessageCount: getMessages(conversationId).filter((m) => m.type === 'HUMAN').length,
						product: productKey,
						featureArea: 'remix',
						remixType: getDynamicUiType(streamMessage.message as MessageAction),
						[PreviewCardRenderStage.STARTED_AT]: performance.now(),
					},
				});
			}
		}

		if (isStreamActionPart(streamMessage)) {
			experienceTracker.updateAttributes({
				name: ExperienceName.PREVIEW_CARD_RENDER,
				attributes: {
					[PreviewCardRenderStage.ACTION_PART_AT]: performance.now(),
					[PreviewCardRenderActionAttribute.ACTION_PART_STATUS]:
						streamMessage.message.status ?? MISSING_ACTION_STATUS,
				},
			});
			const actionData = streamMessage.message.data as { dynamicUiSubtype?: string } | undefined;
			stopInteractionMetricsTimeToFirstActionPart('stop', {
				actionType: 'action-part',
				conversationId,
				actionPartKey: getActionPartKeyForMetrics(
					streamMessage.message.key,
					creationContextParams?.source,
					actionData?.dynamicUiSubtype,
				),
				interactionSource: messageSources?.interactionSource,
				remixType: creationContextParams?.dynamicUiType,
			});
		}

		const actionKey = streamMessage.message.key;
		const lastAction = findLastAssistantAction(
			getMessages(conversationId),
			(action) => action.key === actionKey,
		);

		if (lastAction && isAccumulatedAction(actionKey)) {
			const isAccumulatedActionType = getAccumulatedActionTypeGuard(actionKey);
			if (isAccumulatedActionType(streamMessage.message.data)) {
				const primaryAccumulatedData = getPrimaryAccumulatedValue(lastAction);
				if (primaryAccumulatedData?.value) {
					streamMessage.message.data.previousAccumulatedData = primaryAccumulatedData.value;
				}
			}
		}

		processMessageStreamAction?.(
			tmpAssistantMessageId,
			streamMessage,
			traceId,
			trackingAttributes.requestId,
			undefined,
			{
				singleInstrumentationId: trackingAttributes.instrumentationId,
				// Forward the originating experience so the editor receiver
				// can auto-open the Post Stream Review "Review moment" only for inline edits.
				experience: creationContextParams?.experience,
			},
			agentId
				? {
						id: agentId,
						name: agentName ?? '',
						identityAccountId: agentIdentityAccountId ?? null,
					}
				: undefined,
		);

		promptStreamHook?.onActionPart?.(
			streamMessage,
			tmpAssistantMessageId,
			conversationId,
			openChat,
		);

		const updatedMessages = addActionPart({
			chat: getMessages(conversationId),
			messageId: tmpAssistantMessageId,
			actionPart: streamMessage,
			actionUpdaterOverride: customActionUpdater,
		});

		if (isStreamActionPrepare(streamMessage)) {
			const actionMessage = streamMessage.message;
			experienceTracker.updateAttributes({
				name: ExperienceName.PREVIEW_CARD_RENDER,
				attributes: {
					[PreviewCardRenderActionAttribute.ACTION_PREPARE_STATUS]:
						actionMessage.status ?? MISSING_ACTION_STATUS,
				},
			});
			// Abort the remix-edit-existing UFO experience if the streamed action is not remix.
			if (!isRemixGeneratedFlow(actionMessage)) {
				remixEditExistingExperience.abort({ metadata: { reason: 'non-remix-action' } });
			}
			setMessages(conversationId, [
				...updatedMessages,
				{ type: 'ACTION_PREPARE', action: actionMessage },
			]);
		} else {
			setMessages(conversationId, updatedMessages);
		}
	}

	const canHandleStreamActionClassification = fg('platform_editor_ai_promptversion_in_analytics');
	if (
		isStreamActionClassification(streamMessage) &&
		canHandleStreamActionClassification &&
		streamMessage.message.metadata
	) {
		// Send ACTION_CLASSIFICATION metadata to the Editor Rovo bridge
		processMessageStreamActionClassification?.(
			tmpAssistantMessageId,
			streamMessage,
			traceId,
			trackingAttributes.requestId,
			{
				singleInstrumentationId: trackingAttributes.instrumentationId,
			},
		);
	}

	if (isStreamActionComplete(streamMessage)) {
		hasReceivedActionComplete = true;
		processMessageStreamComplete?.(
			tmpAssistantMessageId,
			streamMessage,
			traceId,
			trackingAttributes.requestId,
			{
				singleInstrumentationId: trackingAttributes.instrumentationId,
				// Forward the originating experience so the editor receiver
				// can auto-open the Post Stream Review "Review moment" only for inline edits.
				experience: creationContextParams?.experience,
			},
		);
		const actionMessage = streamMessage.message;

		let messages = getMessages(conversationId);

		// Insert the completed action only when it isn't already on the message — those actions arrived
		// with no ACTION_PART chunks, so nothing has added them yet. When the action did stream,
		// re-running the accumulator on the completed snapshot would corrupt its accumulated content,
		// so leave it untouched.
		const { invocationId } = actionMessage;
		const isActionAlreadyOnMessage = Boolean(
			invocationId &&
			findLastAssistantAction(messages, (action) => action.invocationId === invocationId),
		);

		if (
			!isActionAlreadyOnMessage &&
			FeatureGates.getExperimentValue<boolean>('cwr-be-based-multi-creation-v0', 'isEnabled', false)
		) {
			messages = addActionPart({
				chat: messages,
				messageId: tmpAssistantMessageId,
				actionPart: streamMessage,
				// The completed action is a full snapshot, so replace rather than accumulate — this avoids
				// double-appending the primary document if a match is ever found.
				actionUpdaterOverride: (_prevAction, nextAction) => nextAction,
			});
		}

		setMessages(conversationId, [...messages, { type: 'ACTION_COMPLETE', action: actionMessage }]);
		experienceTracker.updateAttributes({
			name: ExperienceName.PREVIEW_CARD_RENDER,
			attributes: {
				[PreviewCardRenderStage.ACTION_COMPLETE_AT]: performance.now(),
				[PreviewCardRenderActionAttribute.ACTION_COMPLETE_STATUS]:
					streamMessage.message.status ?? MISSING_ACTION_STATUS,
				// Sanity check that one message did not stream chunks under multiple action keys.
				[PreviewCardRenderActionAttribute.ACTION_COMPLETE_KEY]: streamMessage.message.key,
			},
		});
		experienceTracker.succeed({ name: ExperienceName.AIFC_MESSAGE });
	}

	return { hasReceivedActionClassification, hasReceivedActionComplete };
};
